import { ASSET_FIELDS_WITHOUT_SYS, SIMPLE_DATA_CONTAINER_TYPES } from './constants';
import { adaptAsset, adaptEntry, adaptRichText, resolveAssetSource, resolveEntrySource } from './transforms';
import { hasSysIdAndSlug, isDocument, isEntryWithSysAndSlug, isMediaAssetLike } from './type-guards';
import type { IncludesMap } from './types';
import { ENTRY_NATIVE_TYPENAMES } from 'utils/entryNativeTypenames';
import type { ModuleTypeName } from 'utils/modules';

type EntryMutator = (entry: Record<string, unknown>) => void;

interface ItemsCollection {
  items?: unknown[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasCollectionItems(value: unknown): value is ItemsCollection {
  return isRecord(value) && Array.isArray(value.items) && value.items.length > 0;
}

function isRichTextFieldObject(value: Record<string, unknown>): boolean {
  return 'json' in value && 'links' in value;
}

function getEntryFields(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const fields = (value as Record<string, unknown>).fields;
  return fields && typeof fields === 'object' && !Array.isArray(fields)
    ? (fields as Record<string, unknown>)
    : undefined;
}

function getResolvedAsset(value: unknown, includesMap: IncludesMap): Record<string, unknown> | undefined {
  const asset = resolveAssetSource(value, includesMap);
  if (!asset) {
    return undefined;
  }

  return isMediaAssetLike(asset) ? asset : adaptAsset(asset);
}

function getResolvedInternalPage(
  value: unknown,
  includesMap: IncludesMap,
): { sys: { id: string }; slug: string } | undefined {
  const pageEntry = resolveEntrySource(value, includesMap);
  if (!pageEntry) {
    return undefined;
  }

  const fields = getEntryFields(pageEntry);
  if (!fields) {
    return undefined;
  }

  const slug = fields.slug;
  const sys = pageEntry.sys;
  if (!sys || typeof sys !== 'object' || Array.isArray(sys)) {
    return undefined;
  }

  const id = (sys as Record<string, unknown>).id;
  if (typeof slug !== 'string' || typeof id !== 'string') {
    return undefined;
  }

  return {
    slug,
    sys: { id },
  };
}

function normalizeSimpleDataContainerNode(node: Record<string, unknown>): void {
  const typename = node.__typename as string | undefined;
  const contentTypeId = typename ? typename.charAt(0).toLowerCase() + typename.slice(1) : undefined;

  if (!contentTypeId || !SIMPLE_DATA_CONTAINER_TYPES.includes(contentTypeId)) {
    return;
  }

  delete node.__typename;
  delete node.contentfulMetadata;

  if (
    contentTypeId === 'footerColumn' ||
    contentTypeId === 'customQueryStringParameter' ||
    contentTypeId === 'headerLogoItem' ||
    contentTypeId === 'iconBullet' ||
    contentTypeId === 'navMainLink' ||
    contentTypeId === 'subnavLink' ||
    contentTypeId === 'calloutIconCard' ||
    contentTypeId === 'calloutImageCard' ||
    contentTypeId === 'supportedAspectRatios' ||
    contentTypeId === 'aspectRatio' ||
    contentTypeId === 'teaserLinkItem' ||
    contentTypeId === 'hyperlinkedMedia'
  ) {
    delete node.sys;
  }
}

function minimizeShortcodeNode(node: Record<string, unknown>): boolean {
  if (node.__typename !== 'Shortcode') {
    return false;
  }

  for (const key of Object.keys(node)) {
    if (key !== '__typename') {
      delete node[key];
    }
  }

  return true;
}

function stripEmptyAccordionHyperlinkKeysFromNode(node: Record<string, unknown>, inAccordionModule: boolean): void {
  if (
    !inAccordionModule ||
    (node.__typename !== 'AccordionItem' && !('accordionCopy' in node && 'accordionLabelRte' in node))
  ) {
    return;
  }

  const richTextKeys = ['accordionCopy', 'accordionLabelRte'] as const;
  for (const key of richTextKeys) {
    const richTextField = node[key];
    if (!richTextField || typeof richTextField !== 'object' || Array.isArray(richTextField)) continue;

    const links = (richTextField as Record<string, unknown>).links;
    if (!links || typeof links !== 'object' || Array.isArray(links)) continue;

    const entries = (links as Record<string, unknown>).entries;
    if (!entries || typeof entries !== 'object' || Array.isArray(entries)) continue;

    const hyperlink = (entries as Record<string, unknown>).hyperlink;
    if (Array.isArray(hyperlink) && hyperlink.length === 0) {
      delete (entries as Record<string, unknown>).hyperlink;
    }
  }
}

function normalizeMediaCopyForPageSlugNode(
  node: Record<string, unknown>,
  isOnlineBanking: boolean,
  isComponentLibrary: boolean,
): void {
  if (node.__typename !== 'MediaCopy' || (!isOnlineBanking && !isComponentLibrary)) {
    return;
  }

  if (!('sliderEntryCollection' in node)) {
    node.sliderEntryCollection = { items: [] };
  }

  if ('hyperlinkedImages' in node) {
    delete node.hyperlinkedImages;
  }
}

function normalizeAccordionInlineShortcodesNode(node: Record<string, unknown>, shouldMinimize: boolean): void {
  if (!shouldMinimize || node.__typename !== 'AccordionItem') {
    return;
  }

  const accordionCopy = node.accordionCopy;
  if (!accordionCopy || typeof accordionCopy !== 'object' || Array.isArray(accordionCopy)) {
    return;
  }

  const links = (accordionCopy as Record<string, unknown>).links;
  if (!links || typeof links !== 'object' || Array.isArray(links)) {
    return;
  }

  const entries = (links as Record<string, unknown>).entries;
  if (!entries || typeof entries !== 'object' || Array.isArray(entries)) {
    return;
  }

  const inline = (entries as Record<string, unknown>).inline;
  if (!Array.isArray(inline)) {
    return;
  }

  (entries as Record<string, unknown>).inline = inline.map(entry => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return entry;
    }

    const inlineEntry = entry as Record<string, unknown>;
    if (inlineEntry.__typename !== 'Shortcode') {
      return entry;
    }

    return { __typename: 'Shortcode' };
  });
}

/**
 * Detects entries returned from `adaptEntry` with `preserveEntryNativeModules`
 * on. Their `fields` subtree must stay raw, so the page-adapter normalizer walk
 * skips descending into it.
 */
function isPreservedEntryNative(obj: Record<string, unknown>): boolean {
  const typename = obj.__typename;
  if (typeof typename !== 'string') return false;
  if (!ENTRY_NATIVE_TYPENAMES.has(typename as ModuleTypeName)) return false;
  return isRecord(obj.fields);
}

export function walkEntryGraph(entry: Record<string, unknown>, mutators: EntryMutator[]): void {
  const visited = new WeakSet<object>();

  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) {
        visit(item);
      }
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);

    for (const mutator of mutators) {
      mutator(obj);
    }

    const skipFields = isPreservedEntryNative(obj);
    for (const [key, nestedValue] of Object.entries(obj)) {
      if (skipFields && key === 'fields') continue;
      visit(nestedValue);
    }
  };

  visit(entry);
}

export function normalizeUndefinedToNull(obj: unknown): unknown {
  if (obj === undefined) return null;
  if (obj === null || typeof obj !== 'object') return obj;

  const visited = new WeakSet<object>();
  const visit = (value: Record<string, unknown>): void => {
    if (visited.has(value)) return;
    visited.add(value);

    for (const key of Object.keys(value)) {
      const v = value[key];
      if (v === undefined) {
        value[key] = null;
      } else if (v !== null && typeof v === 'object') {
        if (Array.isArray(v)) {
          for (let i = 0; i < v.length; i++) {
            if (v[i] === undefined) {
              v[i] = null;
            } else if (v[i] !== null && typeof v[i] === 'object') {
              visit(v[i] as Record<string, unknown>);
            }
          }
        } else {
          visit(v as Record<string, unknown>);
        }
      }
    }
  };

  visit(obj as Record<string, unknown>);
  return obj;
}

/**
 * Removes __typename from simple data container types.
 * GraphQL doesn't add __typename to certain simple content types - they're treated as plain objects.
 */
export function removeTypenameFromDataContainers(entry: Record<string, unknown>): void {
  const visited = new WeakSet<object>();
  const visit = (node: Record<string, unknown>): void => {
    if (visited.has(node)) return;
    visited.add(node);

    normalizeSimpleDataContainerNode(node);

    // Rich text fields (objects with both `json` and `links`) store inline/block/hyperlink
    // entries under `links.entries.inline` etc. Those entries need their __typename preserved
    // so that renderEmbeddedEntry can switch on it. Skip the `links` branch entirely.
    const isRichTextField = isRichTextFieldObject(node);

    // Recursively process nested entries
    for (const [key, value] of Object.entries(node)) {
      if (isRichTextField && key === 'links') continue;
      if (value && typeof value === 'object') {
        if (Array.isArray(value)) {
          for (const item of value) {
            if (item && typeof item === 'object') {
              visit(item as Record<string, unknown>);
            }
          }
        } else if ('items' in value && Array.isArray((value as { items: unknown }).items)) {
          const collection = value as { items: unknown[] };
          for (const item of collection.items) {
            if (item && typeof item === 'object') {
              visit(item as Record<string, unknown>);
            }
          }
        } else {
          visit(value as Record<string, unknown>);
        }
      }
    }
  };
  visit(entry);
}

export function stripCardTypenameInCardCarousel(entry: Record<string, unknown>): void {
  if (entry.__typename === 'CardCarousel') {
    const cards = entry.cards;
    if (cards && typeof cards === 'object' && !Array.isArray(cards)) {
      const items = (cards as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        for (const item of items) {
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
          const card = item as Record<string, unknown>;
          if (card.__typename === 'Card') {
            delete card.__typename;
          }
        }
      }
    }
  }
}

/**
 * Removes editorial/internal fields from all entries.
 * GraphQL queries typically exclude internal editorial fields like internalName.
 */
export function applyEditorialFieldNormalization(entry: Record<string, unknown>): void {
  // Remove editorial fields that GraphQL doesn't return
  const editorialFields = ['internalName'];

  for (const field of editorialFields) {
    if (field in entry) {
      delete entry[field];
    }
  }

  // Remove fields that are present in REST but not queried/aliased in GraphQL fixtures
  if (entry.__typename === 'MediaCopy' && 'imageAspectRatio' in entry) {
    delete entry.imageAspectRatio;
  }
  if (entry.__typename === 'MediaCopy' && 'showChimeLogoInEyebrow' in entry) {
    delete entry.showChimeLogoInEyebrow;
  }
  if (entry.__typename === 'CalloutGrid' && 'headline' in entry) {
    delete entry.headline;
  }
  if (entry.__typename === 'HeaderCta' && 'showChimeLogo' in entry) {
    delete entry.showChimeLogo;
  }
  if (entry.__typename === 'VideoItem' && 'name' in entry) {
    delete entry.name;
  }
  if (entry.__typename === 'VideoItem' && 'playbackSetting' in entry) {
    delete entry.playbackSetting;
  }
  if (entry.__typename === 'SupportedAspectRatios' && 'name' in entry) {
    delete entry.name;
  }
  if (entry.__typename === 'AspectRatio' && 'name' in entry) {
    delete entry.name;
  }
  if (entry.__typename === 'CardsExpandableItem' && 'name' in entry) {
    delete entry.name;
  }
  if (entry.__typename === 'IconBullet' && 'name' in entry) {
    delete entry.name;
  }
  if (entry.__typename === 'CardsStackedItem' && 'variant' in entry) {
    delete entry.variant;
  }
  if (entry.__typename === 'CalloutGridCard' && 'variant' in entry) {
    delete entry.variant;
  }
  if (entry.__typename === 'CalloutGridCard' && 'copyJustification' in entry) {
    delete entry.copyJustification;
  }
}

export function removeEditorialFields(entry: Record<string, unknown>): void {
  walkEntryGraph(entry, [applyEditorialFieldNormalization]);
}

/**
 * Removes experiment-related fields from the final output when the experiment
 * pipeline does not need to preserve them (e.g. for Redis-cached output).
 */
export function removeExperimentFields(entry: Record<string, unknown>): void {
  const experimentFields = [
    'nt_experiences',
    'nt_experiencesCollection',
    'ntExperiencesCollection',
    'statsigExperiments',
    'statsigExperimentsCollection',
  ];
  for (const field of experimentFields) {
    if (field in entry) {
      delete entry[field];
    }
  }
}

export function annotateNestedExperiments(entry: Record<string, unknown>): void {
  if ('hasExperiments' in entry) {
    return;
  }

  if (Array.isArray(entry.nt_experiences) && entry.nt_experiences.length > 0) {
    entry.hasExperiments = true;
    return;
  }

  if (
    hasCollectionItems(entry.nt_experiences) ||
    hasCollectionItems(entry.nt_experiencesCollection) ||
    hasCollectionItems(entry.ntExperiencesCollection)
  ) {
    entry.hasExperiments = true;
  }
}

const typesWithoutSys = new Set(['CardsIconItem', 'CardsImageItem']);

export function removeSysFromContentTypes(entry: Record<string, unknown>): void {
  const typename = entry.__typename as string | undefined;
  if (typename && typesWithoutSys.has(typename)) {
    delete entry.sys;
  }
}

export function normalizeCardsIconItemCardIcon(entry: Record<string, unknown>): void {
  if (entry.__typename === 'CardsIconItem') {
    const cardIcon = entry.cardIcon;
    if (cardIcon && typeof cardIcon === 'object' && !Array.isArray(cardIcon)) {
      const icon = cardIcon as Record<string, unknown>;
      const url = icon.url;
      const size = icon.size;
      const description = icon.description;
      if (typeof url === 'string' && typeof size === 'number' && typeof description === 'string') {
        entry.cardIcon = { url, size, description };
      }
    }
  }
}

const TYPES_WITH_IMAGE_WITHOUT_SYS = new Set([
  'BadgeOverlay',
  'HyperlinkedMedia',
  'Logo',
  'CalloutGridCard',
  'CalloutImageCard',
  'HeaderLogoItem',
  'Author',
  'NotificationOverlay',
  'TeaserLinkItem',
]);

function removeSysFromAssetField(entry: Record<string, unknown>, key: string): void {
  const value = entry[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;
  const asset = value as Record<string, unknown>;
  if ('sys' in asset) {
    delete asset.sys;
  }
  if (Object.keys(asset).length === 0) {
    entry[key] = null;
  }
}

export function removeSysFromAssetFields(entry: Record<string, unknown>): void {
  const typename = entry.__typename as string | undefined;

  if (typename && TYPES_WITH_IMAGE_WITHOUT_SYS.has(typename)) {
    removeSysFromAssetField(entry, 'image');
  }

  if (typename === 'VideoItem') {
    removeSysFromAssetField(entry, 'video');
  }

  if (typename === 'CardsStackedItem') {
    removeSysFromAssetField(entry, 'iconAsset');
  }

  for (const key of Object.keys(entry)) {
    if (ASSET_FIELDS_WITHOUT_SYS.has(key)) {
      removeSysFromAssetField(entry, key);
    }
  }
}

/**
 * Normalizes content-type-specific optional fields to null (GraphQL behavior).
 * Different content types have different optional fields that GraphQL returns as null.
 */
const OPTIONAL_FIELDS_BY_TYPE: Record<string, string[]> = {
  AnimatedMarquee: ['cta', 'ctaStyle'],
  Author: ['internalPage', 'url'],
  BentoBox: [
    'eyebrowRte',
    'largeCard',
    'schema',
    'copy',
    'headline',
    'showFiveStarEyebrow',
    'sectionName',
    'layout',
    'collapseMobileCopy',
  ],
  Card: [
    'eyebrow',
    'iconImage',
    'desktopMedia',
    'desktopAspectRatio',
    'mobileMedia',
    'mobileAspectRatio',
    'cta',
    'sectionName',
    'color',
  ],
  CardCarousel: ['sectionName'],
  BentoBoxCardLarge: ['headlineRte'],
  CalloutGrid: ['schema', 'sectionName'],
  CalloutGridCard: [
    'aspectRatio',
    'headline',
    'headlineSmallRte',
    'copy',
    'link',
    'attributionRte',
    'iconName',
    'image',
  ],
  CalloutImageCard: ['headline', 'headlineSmallRte', 'copy', 'attributionRte', 'copyJustification'],
  CalloutImage: ['headline', 'sectionName'],
  CalloutIcon: ['headline', 'schema', 'sectionName', 'copyJustification'],
  CalloutIconCard: ['attributionRte', 'copy', 'headline', 'headlineSmallRte', 'link', 'icon'],
  Compare: ['schema', 'sectionName', 'highlightedColumnIndex', 'highlightColor'],
  Cta: ['internalPage', 'variant', 'target'],
  CopyAddOnWithButton: [
    'schema',
    'variant',
    'alignment',
    'transparentBackground',
    'pinCtaAboveFold',
    'headline',
    'sectionName',
  ],
  Faq: ['schema', 'sectionName', 'color', 'headline'],
  FeaturedBanner: ['pinned'],
  HeaderLogo: ['pinned', 'sectionName'],
  HeaderLogoItem: ['image', 'aspectRatio', 'icon', 'illustration'],
  Features: ['transparentBackground', 'fullWidth', 'mobileOrientation', 'title'],
  HubSpot: ['schema', 'sectionName'],
  DisclosureAddOn: ['color', 'margins', 'keyLine', 'sectionName'],
  DisclosureGroupings: ['schema', 'sectionName'],
  Disclosure: ['copy', 'symbol'],
  Eyebrow: ['alternateLogo'],
  HeroCarousel: ['scrollCta', 'sectionName'],
  HomepageHero: [
    'desktopImage',
    'desktopAspectRatio',
    'mobileImage',
    'mobileAspectRatio',
    'sectionName',
    'variant',
    'focusDisclaimers',
  ],
  HyperlinkedMedia: ['link'],
  HeroConsolidated: [
    'allowPromoCodeEntry',
    'color',
    'copy',
    'copySize',
    'cta',
    'disclaimerModal',
    'disclaimers',
    'desktopMedia',
    'desktopAspectRatio',
    'emailInputButtonText',
    'eyebrowAlternateLogo',
    'eyebrowText',
    'imageOverlay',
    'mobileMedia',
    'mobileAspectRatio',
    'margins',
    'mobileMediaLocation',
    'notificationOverlayDesktopPosition',
    'promoCodeInputButtonText',
    'promoCodes',
    'schema',
    'scrollCta',
    'sectionName',
    'headlineSize',
    'video',
  ],
  Link: ['internalPage', 'class', 'id', 'url'],
  LinkedIcon: ['internalPage', 'icon', 'labelPlacement', 'showLabel'],
  Launchpad: ['schema', 'sectionName'],
  ImpactHeadline: ['copy', 'schema', 'sectionName', 'prebuiltHeadline', 'richTextHeadline', 'emailInputButtonText'],
  Linebreak: ['testId'],
  SectionNavigation: ['nav'],
  ContentBlock: ['eyebrowAlternateLogo', 'eyebrow', 'eyebrowChimeLogo', 'copy', 'cta', 'sectionName', 'alignment'],
  CardsIcon: ['schema', 'sectionName'],
  CardsImage: ['schema', 'sectionName'],
  CardsImageItem: ['imageLayout', 'cardImage', 'cardHeadlineRte'],
  CardsGridComplex: ['cardLayout', 'schema'],
  CardsGridComplexItem: ['eyebrow', 'eyebrowRte', 'topRightBadge', 'sectionName'],
  CardsGridSimpleItem: ['icon', 'cta', 'sectionName'],
  CardsIconItem: ['cardIcon', 'cardHeadlineRte', 'imageLayout', 'jumpToCta', 'showFiveStarEyebrow', 'textEyebrowRte'],
  CardsStacked: ['disclosure', 'schema', 'sectionName', 'cardLayout', 'color'],
  CardsStackedItem: [
    'desktopMedia',
    'desktopAspectRatio',
    'mobileMedia',
    'mobileAspectRatio',
    'cta',
    'iconAsset',
    'topRightBadge',
    'eyebrow',
    'shortDescription',
    'desktopAnimation',
    'mobileAnimation',
    'chimeKitIllustrationToken',
    'sectionName',
    'color',
    'badgeOverlayAlignment',
    'copyAlignment',
    'bulletStyle',
  ],
  Accordion: ['aspectRatio', 'schema', 'headline', 'copy', 'sectionName', 'media', 'color'],
  AccordionItem: ['sectionName', 'accordionLabelRte'],
  BentoBoxCard: ['aspectRatio', 'icon', 'copy', 'cardHeadlineRte'],
  MediaCopy: [
    'headlineEyebrow',
    'eyebrowAlternateLogo',
    'notificationOverlay',
    'aspectRatio',
    'topRightBadge',
    'schema',
    'eyebrowText',
    'eyebrowChimeLogo',
    'containedColor',
    'sectionName',
    'mobileAlignment',
    'mobileCopyJustification',
    'notificationOverlayDesktopPosition',
    'copy',
    'cta',
  ],
  MapLocator: ['sectionName'],
  Nav: ['resourcesInternalPage', 'resourcesUrl', 'variant'],
  Subnav: ['schema', 'sectionName'],
  NavSubMenu: ['titleOverride'],
  NavSubLink: ['icon'],
  Page: ['pageColor', 'showFooterKeyline', 'pageVisualSettings'],
  Resource: ['aspectRatio', 'resourceLabelRte', 'resourcePage', 'url'],
  ResourcesItgBranded: ['cta', 'keyline', 'schema'],
  RichTextEditor: ['sectionName'],
  TeaserLinks: ['schema', 'sectionName', 'keyline', 'margins'],
  TeaserLinkItem: ['internalPage', 'url', 'image'],
  Testimonial: ['disclaimer', 'schema', 'sectionName', 'variant'],
  TestimonialItem: ['aspectRatio', 'attributionRte', 'cta', 'media'],
  CardsExpandable: ['schema', 'sectionName', 'color'],
  CardsExpandableItem: ['aspectRatio'],
  CardsVerticalScroll: ['schema', 'sectionName', 'displayDividers', 'offsetMobileScrollTop'],
  CardsVerticalScrollItem: [
    'headerImage',
    'headerImageOnLight',
    'mobileMedia',
    'desktopAspectRatio',
    'mobileAspectRatio',
    'cta',
    'bulletStyle',
    'mobileCollapsedLines',
  ],
  IconBullet: ['icon', 'iconName'],
  ToggleItem: ['tabCopy'],
  ResponsiveMedia: ['desktopImage', 'mobileImage'],
  VideoItem: ['video', 'videoUrl', 'overlayLabel'],
};

export function normalizeOptionalFields(entry: Record<string, unknown>): void {
  walkEntryGraph(entry, [applyOptionalFieldNormalization]);
}

export function applyOptionalFieldNormalization(entry: Record<string, unknown>): void {
  const typename = entry.__typename as string | undefined;
  const fields = typename ? OPTIONAL_FIELDS_BY_TYPE[typename] : undefined;
  const shouldApplyOptionalFields =
    !typename ||
    typename !== 'Page' ||
    // Only apply Page-wide optional fields to full Page objects (not minimal link targets in rich text)
    'modulesCollection' in entry ||
    'headerBannerCollection' in entry ||
    'seoTitle' in entry ||
    'title' in entry;

  if (fields && shouldApplyOptionalFields) {
    for (const field of fields) {
      if (!(field in entry)) {
        entry[field] = null;
      }
    }
  }

  // Some boolean fields default to false in GraphQL fixtures rather than null.
  if (typename === 'MediaCopy') {
    if (!('eyebrowChimeLogo' in entry)) {
      entry.eyebrowChimeLogo = false;
    }
    if (!('sliderEntryCollection' in entry)) {
      entry.sliderEntryCollection = { items: [] };
    }
  }
}

export function normalizeEditorialAndOptionalFields(entry: Record<string, unknown>): void {
  walkEntryGraph(entry, [applyEditorialFieldNormalization, applyOptionalFieldNormalization]);
}

/**
 * Ensures DisclosureGroupings.disclosures.items[] matches recorded GraphQL fixture shape.
 *
 * Recorded fixtures expect `disclosureItem` to be present (null if missing) for disclosures
 * inside DisclosureGroupings.disclosures, but NOT for other Disclosure shapes (e.g. rich text embeds).
 */
export function normalizeDisclosureGroupingsDisclosures(entry: Record<string, unknown>): void {
  if (entry.__typename === 'DisclosureGroupings') {
    const disclosures = entry.disclosures;
    if (disclosures && typeof disclosures === 'object' && !Array.isArray(disclosures)) {
      const items = (disclosures as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
          const disclosure = item as Record<string, unknown>;
          if (disclosure.__typename !== 'Disclosure') continue;

          const hasDisclosureItem = 'disclosureItem' in disclosure;
          const hasDisclosureSymbol = 'disclosureSymbol' in disclosure;
          if (hasDisclosureItem && hasDisclosureSymbol) continue;

          items[i] = {
            ...disclosure,
            disclosureItem: hasDisclosureItem ? disclosure.disclosureItem : null,
            disclosureSymbol: hasDisclosureSymbol ? disclosure.disclosureSymbol : null,
          };
        }
      }
    }
  }
}

/**
 * Ensures DisclosureAddOn.disclosuresCollection.items[] matches recorded GraphQL fixture shape.
 *
 * DisclosureAddOn contains a mixed collection of Disclosure and Modal types.
 * Disclosure items expect `disclosureItem` and `disclosureSymbol` to be present (null if missing).
 */
export function normalizeDisclosureAddOnDisclosures(entry: Record<string, unknown>): void {
  if (entry.__typename === 'DisclosureAddOn') {
    const disclosuresCollection = entry.disclosuresCollection;
    if (disclosuresCollection && typeof disclosuresCollection === 'object' && !Array.isArray(disclosuresCollection)) {
      const items = (disclosuresCollection as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
          const disclosure = item as Record<string, unknown>;
          // Only normalize Disclosure types, not Modal types
          if (disclosure.__typename !== 'Disclosure') continue;

          const hasDisclosureItem = 'disclosureItem' in disclosure;
          const hasDisclosureSymbol = 'disclosureSymbol' in disclosure;
          if (hasDisclosureItem && hasDisclosureSymbol) continue;

          items[i] = {
            ...disclosure,
            disclosureItem: hasDisclosureItem ? disclosure.disclosureItem : null,
            disclosureSymbol: hasDisclosureSymbol ? disclosure.disclosureSymbol : null,
          };
        }
      }
    }
  }
}

/**
 * Recorded fixtures expect different Disclosure shapes depending on context.
 *
 * For Disclosures embedded in ContentBlock.copy rich text, fixtures include:
 * - disclosureItem: null
 * - disclosureSymbol: null
 */
export function normalizeContentBlockEmbeddedDisclosures(entry: Record<string, unknown>): void {
  if (entry.__typename === 'ContentBlock') {
    const copy = entry.copy;
    if (copy && typeof copy === 'object' && !Array.isArray(copy)) {
      const links = (copy as Record<string, unknown>).links;
      if (links && typeof links === 'object' && !Array.isArray(links)) {
        const entries = (links as Record<string, unknown>).entries;
        if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
          const inline = (entries as Record<string, unknown>).inline;
          if (Array.isArray(inline)) {
            for (let i = 0; i < inline.length; i++) {
              const item = inline[i];
              if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
              const embedded = item as Record<string, unknown>;
              if (embedded.__typename !== 'Disclosure') continue;

              const hasDisclosureItem = 'disclosureItem' in embedded;
              const hasDisclosureSymbol = 'disclosureSymbol' in embedded;
              if (hasDisclosureItem && hasDisclosureSymbol) continue;

              inline[i] = {
                ...embedded,
                disclosureItem: hasDisclosureItem ? embedded.disclosureItem : null,
                disclosureSymbol: hasDisclosureSymbol ? embedded.disclosureSymbol : null,
              };
            }
          }
        }
      }
    }
  }
}

/**
 * Normalizes `cta` fields that are `Link` entries to match the GraphQL query shape.
 *
 * Some GraphQL fragments select `cta` link fields without `sys` / `__typename`.
 * In those cases the expected shape is a plain object with: url, label, target, id, class.
 */
export function normalizeCtaLinks(entry: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(entry)) {
    if (key === 'cta' && value && typeof value === 'object' && !Array.isArray(value)) {
      const cta = value as Record<string, unknown>;
      if (cta.__typename === 'Link') {
        if (entry.__typename === 'TestimonialItem' || entry.__typename === 'FeaturedBanner') {
          entry.cta = {
            sys: cta.sys,
            internalPage: 'internalPage' in cta ? cta.internalPage : null,
            label: cta.label,
            url: cta.url,
            target: cta.target,
            id: 'id' in cta ? cta.id : null,
            class: 'class' in cta ? cta.class : null,
          };
          continue;
        }
        entry.cta = {
          url: cta.url,
          label: cta.label,
          target: cta.target,
          id: 'id' in cta ? cta.id : null,
          class: 'class' in cta ? cta.class : null,
        };
        continue;
      }

      // Cta entries should be flattened to match GraphQL fragments (no __typename)
      if (cta.__typename === 'Cta') {
        const internalPage = cta.internalPage;
        const normalizedInternalPage =
          internalPage && typeof internalPage === 'object' && !Array.isArray(internalPage)
            ? (() => {
                const obj = internalPage as Record<string, unknown>;
                if (isEntryWithSysAndSlug(obj)) {
                  return { sys: obj.sys, slug: obj.slug };
                }
                return internalPage;
              })()
            : internalPage;

        if (entry.__typename === 'CopyAddOnWithButton') {
          entry.cta = {
            __typename: 'Cta',
            sys: cta.sys,
            internalPage: normalizedInternalPage,
            text: cta.text,
            url: 'url' in cta ? cta.url : null,
            target: 'target' in cta ? cta.target : null,
            variant: 'variant' in cta ? cta.variant : null,
          };
        } else {
          entry.cta = {
            sys: cta.sys,
            text: cta.text,
            url: 'url' in cta ? cta.url : null,
            variant: 'variant' in cta ? cta.variant : null,
            internalPage: normalizedInternalPage,
            target: 'target' in cta ? cta.target : null,
          };
        }
        continue;
      }
    }

    if (key === 'jumpToCta' && value && typeof value === 'object' && !Array.isArray(value)) {
      const cta = value as Record<string, unknown>;
      if (cta.__typename === 'Cta') {
        entry.jumpToCta = {
          text: cta.text,
          url: 'url' in cta ? cta.url : null,
          target: 'target' in cta ? cta.target : null,
        };
        continue;
      }
    }

    if (key === 'scrollCta' && value && typeof value === 'object' && !Array.isArray(value)) {
      const cta = value as Record<string, unknown>;
      if (cta.__typename === 'Cta') {
        const internalPage = cta.internalPage;
        const normalizedInternalPage =
          internalPage && typeof internalPage === 'object' && !Array.isArray(internalPage)
            ? (() => {
                const obj = internalPage as Record<string, unknown>;
                if (isEntryWithSysAndSlug(obj)) {
                  return { sys: obj.sys, slug: obj.slug };
                }
                return internalPage;
              })()
            : internalPage;

        entry.scrollCta = {
          sys: cta.sys,
          internalPage: normalizedInternalPage ?? null,
          text: cta.text,
          url: 'url' in cta ? cta.url : null,
          variant: 'variant' in cta ? cta.variant : null,
        };
        continue;
      }
    }

    if (key === 'ctas' && value && typeof value === 'object' && !Array.isArray(value)) {
      const items = (value as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        for (const item of items) {
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
          const cta = item as Record<string, unknown>;
          if (cta.__typename === 'Cta') {
            delete cta.__typename;
          }
        }
      }
    }
  }
}

/**
 * Normalizes NavMainLink.link to match GraphQL fragments (no `__typename` on Link / internalPage).
 */
export function normalizeNavMainLinks(entry: Record<string, unknown>): void {
  if (
    (entry.__typename === 'NavMainLink' || entry.__typename === 'NavSubLink') &&
    entry.link &&
    typeof entry.link === 'object' &&
    !Array.isArray(entry.link)
  ) {
    const link = entry.link as Record<string, unknown>;
    const internalPage = link.internalPage;

    const normalizedInternalPage =
      internalPage && typeof internalPage === 'object' && !Array.isArray(internalPage)
        ? (() => {
            const obj = internalPage as Record<string, unknown>;
            if (isEntryWithSysAndSlug(obj)) {
              return { sys: obj.sys, slug: obj.slug };
            }
            return internalPage;
          })()
        : internalPage;

    if (link.__typename === 'Link') {
      entry.link = {
        sys: link.sys,
        label: link.label,
        url: 'url' in link ? link.url : null,
        target: link.target,
        id: 'id' in link ? link.id : null,
        class: 'class' in link ? link.class : null,
        internalPage: normalizedInternalPage,
      };
    } else {
      entry.link = {
        ...link,
        internalPage: normalizedInternalPage,
      };
    }
  }
}

/**
 * Normalizes Nav.topBarLink to match GraphQL fragments.
 *
 * Recorded fixtures expect `topBarLink` to be a plain object (no __typename), even though
 * it comes from the LinkedIcon content type.
 */
export function normalizeNavTopBarLink(entry: Record<string, unknown>): void {
  if (
    entry.__typename === 'Nav' &&
    entry.topBarLink &&
    typeof entry.topBarLink === 'object' &&
    !Array.isArray(entry.topBarLink)
  ) {
    const topBarLink = entry.topBarLink as Record<string, unknown>;
    if (topBarLink.__typename === 'LinkedIcon') {
      entry.topBarLink = {
        sys: topBarLink.sys,
        label: 'label' in topBarLink ? topBarLink.label : null,
        url: 'url' in topBarLink ? topBarLink.url : null,
        internalPage: 'internalPage' in topBarLink ? topBarLink.internalPage : null,
        icon: 'icon' in topBarLink ? topBarLink.icon : null,
        iconName: 'iconName' in topBarLink ? topBarLink.iconName : null,
        labelPlacement: 'labelPlacement' in topBarLink ? topBarLink.labelPlacement : null,
        showLabel: 'showLabel' in topBarLink ? topBarLink.showLabel : null,
      };
    }
  }
}

/**
 * Normalizes CalloutIconCard.link to match GraphQL fragments.
 *
 * Recorded fixtures expect `link` to be a plain object (no __typename) with the common Link fields.
 */
export function normalizeCalloutIconCardLinks(entry: Record<string, unknown>): void {
  if (
    (entry.__typename === 'CalloutIconCard' || entry.__typename === 'CalloutGridCard') &&
    entry.link &&
    typeof entry.link === 'object' &&
    !Array.isArray(entry.link)
  ) {
    const link = entry.link as Record<string, unknown>;
    if (link.__typename === 'Link') {
      const sys = link.sys;
      const sysRecord =
        sys && typeof sys === 'object' && !Array.isArray(sys) ? (sys as Record<string, unknown>) : undefined;
      entry.link = {
        sys: sysRecord && typeof sysRecord.id === 'string' ? { id: sysRecord.id } : link.sys,
        internalPage: 'internalPage' in link ? link.internalPage : null,
        label: 'label' in link ? link.label : null,
        url: 'url' in link ? link.url : null,
        target: 'target' in link ? link.target : null,
        id: 'id' in link ? link.id : null,
        class: 'class' in link ? link.class : null,
      };
    }
  }

  if (
    (entry.__typename === 'CalloutIconCard' || entry.__typename === 'CalloutGridCard') &&
    entry.link &&
    typeof entry.link === 'object' &&
    !Array.isArray(entry.link)
  ) {
    const link = entry.link as Record<string, unknown>;
    const payloadKeys = ['internalPage', 'label', 'url', 'target', 'id', 'class'] as const;
    const hasPayload = payloadKeys.some(k => k in link);
    if (!hasPayload) {
      entry.link = null;
    }
  }
}

const linkTargetKeys = new Set(['internalPage', 'resourcePage', 'resourcesInternalPage']);

/**
 * Normalizes `internalPage` link targets across the tree to match GraphQL fragments.
 *
 * Recorded fixtures consistently expect `internalPage` to be a minimal object: { sys, slug }
 * (no __typename and no extra fields).
 */
export function normalizeInternalPageLinks(entry: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(entry)) {
    if (linkTargetKeys.has(key) && hasSysIdAndSlug(value)) {
      entry[key] = { sys: value.sys, slug: value.slug };
    }
  }
}

/**
 * Normalizes an adapted entry to ensure all collection fields match GraphQL output.
 * GraphQL always returns { items: [] } for collections, even when empty.
 * This function ensures the REST adapter output has the same shape.
 */
const collectionFieldsByType: Record<string, string[]> = {
  ArticleGrid: ['articlesCollection'],
  BlogNavigation: ['resourcesCollection', 'navContent'],
  Card: ['hyperlinkedImages'],
  Faq: ['toggleItems'],
  Resources: ['resources'],
  CalloutIcon: ['content'],
  CalloutImage: ['content'],
  CardsIcon: ['cardIconItems'],
  CardsImage: ['cardImageItems'],
  CardsIconCarousel: ['cardItems'],
  CardsImageCarousel: ['cardItems'],
  CardsIconItem: ['hyperlinkedImagesCollection'],
  CardsImageItem: ['hyperlinkedImagesCollection'],
  CardsExpandableItem: ['cardListItems'],
  Features: ['content'],
  HeroConsolidated: ['hyperlinkedMediaCollection'],
  ImpactHeadline: ['ctas'],
  MediaCopy: ['hyperlinkedImagesCollection', 'sliderEntryCollection'],
  ContentBlock: ['socialMedia'],
  SectionDisclosures: ['disclosuresCollection'],
  DisclosureAddOn: ['disclosuresCollection'],
  DisclosureCollection: ['groupingsCollection'],
  DisclosureGroupings: ['disclosures'],
  CalloutGrid: ['cards'],
  CardsVerticalScroll: ['cards'],
  ResourcesItgBranded: ['resources'],
  Subnav: ['linksCollection'],
  PageSection: ['modulesCollection'],
  SectionBanner: ['headerBannerCollection'],
  PageAgreement: ['sectionsCollection'],
};

export function normalizeCollections(adapted: Record<string, unknown>): void {
  const typename = adapted.__typename as string | undefined;
  const fields = typename ? (collectionFieldsByType[typename] ?? []) : [];

  for (const field of fields) {
    if (Array.isArray(adapted[field])) {
      adapted[field] = { items: adapted[field] };
      continue;
    }

    if (!adapted[field] || (typeof adapted[field] === 'object' && !(adapted[field] as Record<string, unknown>).items)) {
      adapted[field] = { items: [] };
    }
  }
}

export function stripEmptyAccordionHyperlinkKeys(adapted: Record<string, unknown>): void {
  const visited = new WeakSet<object>();
  const visit = (value: unknown, inAccordionModule: boolean): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) visit(item, inAccordionModule);
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);
    const nextInAccordionModule = inAccordionModule || obj.__typename === 'Accordion';

    stripEmptyAccordionHyperlinkKeysFromNode(obj, nextInAccordionModule);

    for (const v of Object.values(obj)) {
      visit(v, nextInAccordionModule);
    }
  };

  visit(adapted, false);
}

export function normalizeMediaCopyByPageSlug(adapted: Record<string, unknown>): void {
  const slug = adapted.slug;
  if (typeof slug !== 'string') {
    return;
  }

  const normalizedSlug = slug.startsWith('/') ? slug.slice(1) : slug;
  const isOnlineBanking = normalizedSlug.startsWith('online-banking');
  const isComponentLibrary = normalizedSlug.startsWith('component-library/');

  const visited = new WeakSet<object>();
  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);
    normalizeMediaCopyForPageSlugNode(obj, isOnlineBanking, isComponentLibrary);

    for (const v of Object.values(obj)) {
      visit(v);
    }
  };

  visit(adapted);
}

export function normalizeMediaCopySliderEntryCollection(entry: Record<string, unknown>): void {
  if (entry.__typename === 'MediaCopy') {
    if (!('sliderEntryCollection' in entry)) {
      entry.sliderEntryCollection = { items: [] };
    }
  }
}

export function normalizePageScopedFields(adapted: Record<string, unknown>): void {
  const slug = adapted.slug;
  const normalizedSlug = typeof slug === 'string' ? (slug.startsWith('/') ? slug.slice(1) : slug) : '';
  const isOnlineBanking = normalizedSlug.startsWith('online-banking');
  const isComponentLibrary = normalizedSlug.startsWith('component-library/');

  const visited = new WeakSet<object>();
  const visit = (value: unknown, inAccordionModule: boolean): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) {
        visit(item, inAccordionModule);
      }
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);

    const nextInAccordionModule = inAccordionModule || obj.__typename === 'Accordion';

    normalizeMediaCopyForPageSlugNode(obj, isOnlineBanking, isComponentLibrary);
    normalizeAccordionInlineShortcodesNode(obj, isComponentLibrary);
    stripEmptyAccordionHyperlinkKeysFromNode(obj, nextInAccordionModule);
    normalizeSimpleDataContainerNode(obj);

    if (isComponentLibrary && minimizeShortcodeNode(obj)) {
      return;
    }

    const isRichTextField = isRichTextFieldObject(obj);
    for (const [key, nestedValue] of Object.entries(obj)) {
      if (isRichTextField && key === 'links') {
        continue;
      }
      visit(nestedValue, nextInAccordionModule);
    }
  };

  visit(adapted, false);
}

export function stripCardHeadlineRteFromBentoBoxLargeCard(entry: Record<string, unknown>): void {
  if (
    entry.__typename === 'BentoBox' &&
    entry.largeCard &&
    typeof entry.largeCard === 'object' &&
    !Array.isArray(entry.largeCard)
  ) {
    const card = entry.largeCard as Record<string, unknown>;
    if ('cardHeadlineRte' in card) {
      card.headlineRte = card.cardHeadlineRte;
      delete card.cardHeadlineRte;
    }
  }
}

export function hydrateResourceFieldsFromIncludes(
  adapted: Record<string, unknown>,
  includesMap: IncludesMap,
  processed: WeakSet<object>,
): void {
  const visited = new WeakSet<object>();

  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);

    if (obj.__typename === 'Resource') {
      const original = resolveEntrySource(obj, includesMap);
      const fieldsObj = getEntryFields(original);
      if (fieldsObj) {
        if (
          (!('resourceCopy' in obj) || obj.resourceCopy === null) &&
          'resourceCopy' in fieldsObj &&
          isDocument(fieldsObj.resourceCopy)
        ) {
          obj.resourceCopy = adaptRichText(fieldsObj.resourceCopy, includesMap, processed, {
            parentTypename: 'Resource',
            fieldName: 'resourceCopy',
          });
        }

        if (
          (!('resourceLabelRte' in obj) || obj.resourceLabelRte === null) &&
          'resourceLabelRte' in fieldsObj &&
          isDocument(fieldsObj.resourceLabelRte)
        ) {
          obj.resourceLabelRte = adaptRichText(fieldsObj.resourceLabelRte, includesMap, processed, {
            parentTypename: 'Resource',
            fieldName: 'resourceLabelRte',
          });
        }

        if ((!('resourcePage' in obj) || obj.resourcePage === null) && 'resourcePage' in fieldsObj) {
          const resourcePage = getResolvedInternalPage(fieldsObj.resourcePage, includesMap);
          if (resourcePage) {
            obj.resourcePage = resourcePage;
          }
        }

        if (!('resourceIcon' in obj) && 'resourceIcon' in fieldsObj) {
          const resourceIcon = getResolvedAsset(fieldsObj.resourceIcon, includesMap);
          if (resourceIcon) {
            obj.resourceIcon = resourceIcon;
          } else {
            obj.resourceIcon = adaptEntry(fieldsObj.resourceIcon, includesMap, new WeakSet<object>(), true);
          }
        }
      }
    }

    for (const v of Object.values(obj)) {
      visit(v);
    }
  };

  visit(adapted);
}

export function normalizeShortcodeFragmentsByPageSlug(adapted: Record<string, unknown>): void {
  const slug = adapted.slug;
  if (typeof slug !== 'string') {
    return;
  }
  const normalizedSlug = slug.startsWith('/') ? slug.slice(1) : slug;
  const shouldMinimize = normalizedSlug.startsWith('component-library/');
  if (!shouldMinimize) {
    return;
  }

  const isRichTextLinksObject = (value: unknown): boolean => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const obj = value as Record<string, unknown>;
    const entries = obj.entries;
    if (!entries || typeof entries !== 'object' || Array.isArray(entries)) return false;
    const entriesObj = entries as Record<string, unknown>;
    return (
      (Array.isArray(entriesObj.inline) && entriesObj.inline.length >= 0) ||
      (Array.isArray(entriesObj.block) && entriesObj.block.length >= 0) ||
      (Array.isArray(entriesObj.hyperlink) && entriesObj.hyperlink.length >= 0)
    );
  };

  const visited = new WeakSet<object>();
  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);
    if (minimizeShortcodeNode(obj)) {
      return;
    }

    for (const [k, v] of Object.entries(obj)) {
      // Rich text entries (links.entries.inline/block/hyperlink) have their own, context-specific
      // shortcode fragment behavior in `adaptRichText`. Don't globally minimize those here.
      if (k === 'links' && isRichTextLinksObject(v)) continue;
      visit(v);
    }
  };

  visit(adapted);
}

export function normalizeDisclosuresInToggleItemAccordionCopy(entry: Record<string, unknown>): void {
  if (entry.__typename === 'ToggleItem') {
    const contentCollection = entry.contentCollection;
    if (contentCollection && typeof contentCollection === 'object' && !Array.isArray(contentCollection)) {
      const items = (contentCollection as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        for (const item of items) {
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
          const child = item as Record<string, unknown>;
          if (!('accordionCopy' in child)) continue;
          const accordionCopy = child.accordionCopy;
          if (!accordionCopy || typeof accordionCopy !== 'object' || Array.isArray(accordionCopy)) continue;
          const links = (accordionCopy as Record<string, unknown>).links;
          if (!links || typeof links !== 'object' || Array.isArray(links)) continue;
          const entries = (links as Record<string, unknown>).entries;
          if (!entries || typeof entries !== 'object' || Array.isArray(entries)) continue;
          const inline = (entries as Record<string, unknown>).inline;
          if (!Array.isArray(inline)) continue;
          for (const inlineEntry of inline) {
            if (!inlineEntry || typeof inlineEntry !== 'object' || Array.isArray(inlineEntry)) continue;
            const e = inlineEntry as Record<string, unknown>;
            if (e.__typename === 'Disclosure') {
              if ('disclosureItem' in e) delete e.disclosureItem;
              if ('disclosureSymbol' in e) delete e.disclosureSymbol;
            }
          }
        }
      }
    }
  }
}

export function normalizeAccordionItemAccordionCopyInlineShortcodesByPageSlug(adapted: Record<string, unknown>): void {
  const slug = adapted.slug;
  if (typeof slug !== 'string') return;
  const normalizedSlug = slug.startsWith('/') ? slug.slice(1) : slug;

  if (!normalizedSlug.startsWith('component-library/')) return;

  const visited = new WeakSet<object>();
  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);
    normalizeAccordionInlineShortcodesNode(obj, true);

    for (const v of Object.values(obj)) {
      visit(v);
    }
  };

  visit(adapted);
}

export function stripSysFromHomepageHeroImages(entry: Record<string, unknown>): void {
  if (entry.__typename === 'HomepageHero') {
    const images = entry.images;
    if (images && typeof images === 'object' && !Array.isArray(images)) {
      const items = (images as Record<string, unknown>).items;
      if (Array.isArray(items)) {
        for (const item of items) {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            const asset = item as Record<string, unknown>;
            if ('sys' in asset) delete asset.sys;
          }
        }
      }
    }
  }
}

/**
 * Normalizes PageBlogPostFlexible entries within ArticleGrid.articlesCollection
 * to match the GraphQL output format.
 *
 * GraphQL uses aliases to transform the data:
 * - articleTitle: hero { ... on HeroConsolidated { headline { ... } } }
 * - articleImage: hero { ... on HeroConsolidated { desktopMedia { ... } } }
 * - categories: contentfulMetadata { items: concepts { id } }
 *
 * The REST adapter needs to perform the same transformation for these linked entries.
 * We pass the includesMap to handle circular reference cases where the adapted entry
 * only has minimal fields (sys, slug, __typename) due to circular reference protection.
 */
export function normalizeArticleGridFlexibleBlogItems(
  adapted: Record<string, unknown>,
  includesMap: IncludesMap,
): void {
  const visited = new WeakSet<object>();
  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }

    const obj = value as Record<string, unknown>;
    if (visited.has(obj)) return;
    visited.add(obj);

    // Check if this is an ArticleGrid component
    if (obj.__typename === 'ArticleGrid') {
      const articlesCollection = obj.articlesCollection as { items?: unknown[] } | undefined;
      if (articlesCollection?.items && Array.isArray(articlesCollection.items)) {
        for (let i = 0; i < articlesCollection.items.length; i++) {
          const item = articlesCollection.items[i];
          if (!item || typeof item !== 'object' || Array.isArray(item)) continue;

          const articleItem = item as Record<string, unknown>;
          const rawEntry = resolveEntrySource(articleItem, includesMap);
          const rawFields = getEntryFields(rawEntry);

          // Enrich sys with createdAt from the raw entry for all article types
          const itemSys = articleItem.sys as { id?: string; createdAt?: string } | undefined;
          if (itemSys?.id && !itemSys.createdAt && rawEntry) {
            const rawSys = rawEntry?.sys as { createdAt?: string } | undefined;
            if (rawSys?.createdAt) {
              articleItem.sys = { ...itemSys, createdAt: rawSys.createdAt };
            }
          }

          // Enrich publishDate from the raw entry for all article types
          const itemPublishDate = articleItem.publishDate as string | undefined;
          if (!itemPublishDate && rawFields) {
            const rawPublishDate = rawFields.publishDate as string | undefined;
            if (rawPublishDate) {
              articleItem.publishDate = rawPublishDate;
            }
          }

          // Transform PageBlogPostFlexible entries
          if (articleItem.__typename === 'PageBlogPostFlexible') {
            let hero = articleItem.hero as Record<string, unknown> | undefined;

            if (!hero || hero.__typename !== 'HeroConsolidated') {
              const rawHero =
                resolveEntrySource(articleItem.hero, includesMap) ?? resolveEntrySource(rawFields?.hero, includesMap);
              const adaptedHero = rawHero
                ? adaptEntry(rawHero, includesMap, new WeakSet<object>(), true, true)
                : undefined;
              if (adaptedHero && typeof adaptedHero === 'object' && !Array.isArray(adaptedHero)) {
                hero = adaptedHero as Record<string, unknown>;
              }
            }

            // Create articleTitle from hero.headline (if hero is HeroConsolidated)
            // Per GraphQL alias semantics, the wrapper object should be created when hero matches the type condition,
            // even if the nested property (headline/desktopMedia) is null
            if (hero && hero.__typename === 'HeroConsolidated') {
              articleItem.articleTitle = { headline: hero.headline ?? null };
            } else {
              articleItem.articleTitle = null;
            }

            // Create articleImage from hero.desktopMedia (if hero is HeroConsolidated)
            if (hero && hero.__typename === 'HeroConsolidated') {
              articleItem.articleImage = { desktopMedia: hero.desktopMedia ?? null };
            } else {
              articleItem.articleImage = null;
            }

            // Transform contentfulMetadata.concepts to categories.items
            // GraphQL aliases: categories: contentfulMetadata { items: concepts { id } }
            // For circular reference cases, also check raw entry metadata
            let categories: { items: Array<{ id: string }> } = { items: [] };
            const contentfulMetadata = articleItem.contentfulMetadata as
              | { concepts?: Array<{ id: string }> }
              | undefined;
            if (contentfulMetadata?.concepts && Array.isArray(contentfulMetadata.concepts)) {
              categories = { items: contentfulMetadata.concepts };
            } else if (rawEntry) {
              // Try to get from raw entry metadata for circular reference case
              const rawMetadata = rawEntry.metadata as { concepts?: Array<{ sys: { id: string } }> } | undefined;
              if (rawMetadata?.concepts && Array.isArray(rawMetadata.concepts)) {
                categories = { items: rawMetadata.concepts.map(c => ({ id: c.sys.id })) };
              }
            }
            articleItem.categories = categories;

            // Remove the original hero field since it's been transformed
            delete articleItem.hero;

            // Remove fields that aren't queried in the GraphQL fragment
            delete articleItem.contentfulMetadata;
            delete articleItem.modules;
            delete articleItem.modulesCollection;
            delete articleItem.bottomModules;
            delete articleItem.bottomModulesCollection;
            delete articleItem.nav;
            delete articleItem.footer;
            delete articleItem.disclosures;
            delete articleItem.disclosuresCollection;
            delete articleItem.disclosuresBottom;
            delete articleItem.disclosuresBottomCollection;
            delete articleItem.headerBanner;
            delete articleItem.headerBannerCollection;
            delete articleItem.hasExperiments;
            delete articleItem.schema;
            delete articleItem.seoTitle;
            delete articleItem.seoDescription;
            delete articleItem.seoCanonical;
            delete articleItem.seoOpenGraphTitle;
            delete articleItem.seoOpenGraphDescription;
            delete articleItem.seoOpenGraphImage;
            delete articleItem.seoAllowSearchEnginesToIndex;
            delete articleItem.seoAllowSearchEnginesToFollow;
            delete articleItem.customQueryStringParameters;
            delete articleItem.customQueryStringParametersCollection;
            delete articleItem.pageColor;
            delete articleItem.footerColor;
            delete articleItem.showFooterKeyline;
            delete articleItem.pageVisualSettings;
          }
        }
      }
    }

    // Recursively visit nested objects
    for (const v of Object.values(obj)) {
      visit(v);
    }
  };

  visit(adapted);
}
